/** Options for starting an {@link AudioCapture}. */
export interface AudioCaptureOptions {
  /**
   * The turn's microphone stream (opened by the recorder via `speech/mic.ts`).
   * `AudioCapture` records from it and never stops it.
   */
  stream?: MediaStream;
}

/**
 * Thin wrapper over `MediaRecorder` that records a single utterance from the
 * turn's stream to a `Blob`. Shared by the backend (GCP) provider and the
 * native-mode fallback so capture logic lives in one place.
 */
export class AudioCapture {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];

  async start(options: AudioCaptureOptions): Promise<void> {
    this.chunks = [];
    if (!options.stream) {
      throw new Error('No microphone stream for this turn');
    }
    try {
      const recorder = new MediaRecorder(options.stream);
      recorder.ondataavailable = (event): void => {
        if (event.data.size > 0) {
          this.chunks.push(event.data);
        }
      };
      this.recorder = recorder;
      recorder.start();
    } catch (err) {
      // Callers drop a failed capture without disposing it.
      this.dispose();
      throw err;
    }
  }

  /** Stop recording and resolve with the captured audio blob. */
  stop(): Promise<Blob> {
    const recorder = this.recorder;
    if (!recorder || recorder.state === 'inactive') {
      return Promise.resolve(new Blob([], { type: 'audio/webm' }));
    }
    return new Promise<Blob>((resolve) => {
      recorder.onstop = (): void => {
        resolve(new Blob(this.chunks, { type: 'audio/webm' }));
      };
      recorder.stop();
    });
  }

  /** Stop any active recording. The stream belongs to the recorder. */
  dispose(): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.stop();
    }
    this.recorder = null;
    this.chunks = [];
  }
}
